import * as React from 'react';
import ProgressiveImage from 'react-progressive-image';
import Search from './feature/search/Search';
import Graphs from './feature/graphs/Graphs';
import * as Food from './domain/Food';
import './normalize.css';
import './pages.css';
import './autosuggest.css';
import logo_small from './logo-200w.png';
import logo from './logo-700w.png';

type AppState = {
  searchIntro: boolean,
  searchText: string,
  food: Food.Food | null
  foodWithNutrients: Food.FoodWithNutrients | null
};

function build_url(url: string): string {
  return '//' + window.location.host + '/' + url.replace(/\//, '');
}

function mkTitle(food: Food.Food): string {
  return 'eatnutrients.com - ' + food.longDesc;
}

class App extends React.Component<{}, AppState> {
  searchInput: HTMLInputElement | null;

  constructor(props: Object) {
    super(props);
    this.state = {searchIntro: true, searchText: '', food: null, foodWithNutrients: null};

    this.focusSearch = this.focusSearch.bind(this);
    this.removeSearchIntro = this.removeSearchIntro.bind(this);
    this.searchChange = this.searchChange.bind(this);
    this.setFood = this.setFood.bind(this);
    this.fetchData = this.fetchData.bind(this);
  }

  focusSearch(): void {
    // Explicitly focus the text input using the raw DOM API
    if (this.searchInput) { this.searchInput.focus(); }
  }

  componentDidMount(): void {
    this.focusSearch();

    const path = window.location.pathname;
    const match = path.match(/^\/sources\/sr28\/(.*)$/);
    if (match && match[1]) {
      this.fetchData(path, {food: null}, false);
    }

    window.onpopstate = (event: {state: AppState}): void => {
      if (event.state) {
        const { food, foodWithNutrients } = event.state;
        if (foodWithNutrients) {
          this.setState(event.state);
        } else {
          if (food) {
            this.setFood(food, null, true);
          }
        }
      } else {
        this.setState({searchText: '', food: null, foodWithNutrients: null});
      }
    };
  }

  searchChange(event: React.FormEvent<HTMLInputElement>): void {
    let searchText = event.currentTarget.value;
    this.setState({searchText: searchText});
    if (searchText.length > 0) {
      this.removeSearchIntro(searchText);
    }
  }

  removeSearchIntro(searchText: string): void {
    this.setState({searchIntro: false});
  }

  // This is set from searching
  // Searching returns just the energy values and we can display the Energy graph right away
  // But we must fetch the nutrients to display the nutrient graph
  setFood(food: Food.Food, searchText: string | null, popped?: boolean): void {
    let state = {food: food, foodWithNutrients: null};

    const sr28R: string = '/sources/sr28/';
    const foodUrl: string = sr28R + food.objectID;
    if (searchText !== null) {
      state = Object.assign(state, {searchText});
    }
    if (popped) {
      state = Object.assign(state, {searchText: food.longDesc});
    } else {
      history.pushState(state, mkTitle(food), foodUrl);
    }

    this.setState(state);
    this.fetchData(foodUrl, state);
  }

  fetchData(foodUrl: string, state: {food: Food.Food | null}, searchIntro?: boolean): void {
    fetch(build_url(process.env.PUBLIC_URL + foodUrl + '.json')).then((rsp) => {
      if (!rsp.ok) {
        console.warn('Error: bad response: ' + rsp.statusText);
      } else {
        rsp.json().then((data: Food.FoodWithNutrients) => {
          let addState = {foodWithNutrients: data};
          if (!this.state.food) {
            Object.assign(addState, {food: data});
          }
          if (searchIntro !== undefined) {
            Object.assign(addState, {searchIntro});
          }
          this.setState(addState);
          history.replaceState(Object.assign({}, state, addState), mkTitle(data), foodUrl);
        });
      }
    })
    .catch(error => console.warn('Error: ', error));
  }

  render() {
    const { searchIntro, searchText } = this.state;
    return (
      <div>
        { searchIntro && (
        <div id="pages-container">
          <header id="pages-header">
            <a id="main-image" href="/">
              <ProgressiveImage src={logo} placeholder={logo_small}>
                {(src: string, loading: boolean) => (
                  <img
                    src={src}
                    alt="eatnutrients logo"
                    style={!loading ? {} : { marginLeft: '250px', marginTop: '100px' }}
                  />
                )}
              </ProgressiveImage>
            </a>
            <nav>
              <p>
                <a href="/">Home</a>
                &nbsp;-&nbsp;
                <a href="/pages/faq.html">FAQ</a>
                &nbsp;-&nbsp;
                <a href="/pages/data.html">Data Details</a>
                &nbsp;-&nbsp;
                <a href="/pages/about.html">About</a>
              </p>
            </nav>
          </header>

          <div id="pages-main">
            <div id="homepage-contents">
              <input
                 id="search-box"
                 type="search"
                 ref={(input) => { this.searchInput = input; }}
                 value={searchText}
                 onChange={this.searchChange}
              />
              <input type="submit" value="Search For Food" />
              <p id="loading-search-page-msg" style={{display: 'none'}}>
                Loading ...
              </p>
            </div>
          </div>
        </div>
        ) }

        <div id="message-display" style={{display: 'none'}}>
          <p id="message" />
        </div>

        { !searchIntro && (
            <div id="container">
              <Search searchText={searchText} setFood={this.setFood} />
              { this.state.food && (
                  <Graphs food={this.state.food} foodWithNutrients={this.state.foodWithNutrients} />
              ) }
            </div>
        ) }
      </div>
    );
  }
}

export default App;



// WEBPACK FOOTER //
// ./src/App.tsx